import './styles.css';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// Content base URL - empty for local dev, S3 URL for production
const CONTENT_BASE = import.meta.env.DEV
  ? ''
  : 'https://content.larsolino.io/larsolino.io';

// Check if we're in production (using S3 content)
const IS_PRODUCTION = !import.meta.env.DEV;

/**
 * Decrypt content encrypted with AES-256-GCM
 * Input: base64 encoded salt(16) + iv(12) + authTag(16) + ciphertext
 */
async function decryptContent(encryptedBase64: string, password: string): Promise<string> {
  const data = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));

  const salt = data.slice(0, 16);
  const iv = data.slice(16, 28);
  const authTag = data.slice(28, 44);
  const ciphertext = data.slice(44);

  // Derive key from password using PBKDF2
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  const key = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt']
  );

  // Combine ciphertext and authTag for Web Crypto API
  const ciphertextWithTag = new Uint8Array(ciphertext.length + authTag.length);
  ciphertextWithTag.set(ciphertext);
  ciphertextWithTag.set(authTag, ciphertext.length);

  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv },
    key,
    ciphertextWithTag
  );

  return new TextDecoder().decode(decrypted);
}

/**
 * Show password prompt and return entered password
 */
function showPasswordPrompt(title: string): Promise<string | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'password-overlay';
    overlay.innerHTML = `
      <div class="password-dialog">
        <h3>🔒 ${title}</h3>
        <p>Diese Seite ist passwortgeschützt.</p>
        <input type="password" id="password-input" placeholder="Passwort eingeben" autocomplete="off">
        <div class="password-buttons">
          <button id="password-cancel">Abbrechen</button>
          <button id="password-submit">Entsperren</button>
        </div>
        <p class="password-error" id="password-error"></p>
      </div>
    `;

    document.body.appendChild(overlay);

    const input = document.getElementById('password-input') as HTMLInputElement;
    const submitBtn = document.getElementById('password-submit')!;
    const cancelBtn = document.getElementById('password-cancel')!;

    input.focus();

    const submit = () => {
      const password = input.value;
      overlay.remove();
      resolve(password || null);
    };

    const cancel = () => {
      overlay.remove();
      resolve(null);
    };

    submitBtn.addEventListener('click', submit);
    cancelBtn.addEventListener('click', cancel);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') submit();
      if (e.key === 'Escape') cancel();
    });
  });
}

interface Page {
  slug: string;
  folder: string;
  title: string;
  nav: boolean;
}

// Store pages for later lookup
let pagesCache: Page[] = [];

// Cache all notes for client-side filtering
let allNotesCache: Note[] | null = null;

async function fetchAllNotes(): Promise<Note[]> {
  if (allNotesCache) return allNotesCache;

  // In production, fetch from S3; in dev, fetch from local API
  const apiUrl = IS_PRODUCTION
    ? `${CONTENT_BASE}/api/notes.json`
    : '/api/notes';

  const response = await fetch(apiUrl);
  const data = await response.json();

  // Handle both old format (notes) and new S3 format (items)
  let notes: Note[];
  if (data.items) {
    // S3 format: convert items to notes format
    notes = data.items.map((item: any) => {
      const pathParts = item.path.split('/');
      return {
        slug: item.slug,
        year: pathParts[1] || '',  // notes/2026/slug -> 2026
        folder: item.path.replace('notes/', ''),  // notes/2026/slug -> 2026/slug
        title: item.title,
        content: item.content || '',
        date: item.date,
        image: null,
        type: null,
        tags: item.tags || [],
        images: item.images,
        excerpt: item.excerpt
      };
    });
  } else {
    notes = data.notes;
  }

  allNotesCache = notes;
  return allNotesCache;
}

function filterNotes(notes: Note[], options: { tag?: string | null; limit?: number; offset?: number }): NotesResponse {
  let filtered = notes;

  if (options.tag) {
    filtered = notes.filter(n => n.tags.includes(options.tag!.toLowerCase()));
  }

  const total = filtered.length;
  const offset = options.offset || 0;
  const limit = options.limit || total;
  const sliced = filtered.slice(offset, offset + limit);
  const hasMore = offset + limit < total;

  return { notes: sliced, total, hasMore };
}

interface NoteImage {
  id: string;
  thumb: string;
  srcset: string;
  alt: string;
}

interface Note {
  slug: string;
  year: string;
  folder: string;
  title: string | null;
  content: string;
  date: string | null;
  image: string | null;
  type: string | null;
  tags: string[];
  images?: NoteImage[];  // S3 image data (production only)
  excerpt?: string;
}

interface NotesResponse {
  notes: Note[];
  total: number;
  hasMore: boolean;
}

// Configure marked with custom renderer for external links
marked.use({
  gfm: true,
  breaks: true,
  renderer: {
    link({ href, title, text }) {
      const titleAttr = title ? ` title="${title}"` : '';
      if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
        return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
      }
      return `<a href="${href}"${titleAttr}>${text}</a>`;
    }
  }
});

const contentEl = document.getElementById('content')!;
const navEl = document.getElementById('nav')!;

// State for notes pagination
let notesOffset = 0;
let currentTag: string | null = null;
const NOTES_PER_PAGE = 20;

// Parse frontmatter from markdown content
function parseFrontmatter(content: string): { meta: Record<string, string>; body: string } {
  const match = content.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
  if (!match) {
    return { meta: {}, body: content };
  }

  const meta: Record<string, string> = {};
  const frontmatter = match[1];
  const body = match[2];

  frontmatter.split('\n').forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.slice(0, colonIndex).trim();
      let value = line.slice(colonIndex + 1).trim();
      // Strip surrounding quotes
      if ((value.startsWith('"') && value.endsWith('"')) ||
          (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      }
      meta[key] = value;
    }
  });

  return { meta, body };
}

// Render markdown to HTML
async function renderMarkdown(content: string): Promise<string> {
  const html = await marked.parse(content);
  return DOMPurify.sanitize(html);
}

// Extract images from markdown content
function extractImages(content: string): { images: string[]; contentWithoutImages: string } {
  const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
  const images: string[] = [];
  let match;

  while ((match = imageRegex.exec(content)) !== null) {
    images.push(match[2]); // The URL/path is in group 2
  }

  // Remove image markdown from content
  const contentWithoutImages = content.replace(imageRegex, '').trim();

  return { images, contentWithoutImages };
}

// Process content to group consecutive images into swipers
function processConsecutiveImages(content: string, resolveImagePath: (img: string) => string): string {
  const lines = content.split('\n');
  const result: string[] = [];
  let imageGroup: string[] = [];

  const flushImageGroup = () => {
    if (imageGroup.length === 0) return;

    if (imageGroup.length === 1) {
      // Single image - resolve path and keep as markdown
      const match = imageGroup[0].match(/!\[([^\]]*)\]\(([^)]+)\)/);
      if (match) {
        const resolvedSrc = resolveImagePath(match[2]);
        result.push(`![${match[1]}](${resolvedSrc})`);
      } else {
        result.push(imageGroup[0]);
      }
    } else {
      // Multiple consecutive images - create swiper with lightbox support
      const swiperId = `detail-swiper-${++swiperCounter}`;
      const images = imageGroup.map(imgMd => {
        const match = imgMd.match(/!\[([^\]]*)\]\(([^)]+)\)/);
        return match ? resolveImagePath(match[2]) : '';
      }).filter(Boolean);

      const imagesData = JSON.stringify(images).replace(/"/g, '&quot;');
      const slides = images.map((src, idx) =>
        `<div class="swiper-slide"><img src="${src}" alt="" loading="lazy" data-index="${idx}"></div>`
      ).join('');

      result.push(`<div class="note-detail-slideshow swiper" id="${swiperId}" data-images="${imagesData}">
        <div class="swiper-wrapper">${slides}</div>
        <div class="swiper-pagination"></div>
        <div class="swiper-button-prev"></div>
        <div class="swiper-button-next"></div>
      </div>`);
    }
    imageGroup = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();
    const isImage = /^!\[([^\]]*)\]\(([^)]+)\)$/.test(trimmed);

    if (isImage) {
      imageGroup.push(trimmed);
    } else {
      flushImageGroup();
      // Resolve single inline images too
      const processedLine = line.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_match, alt, src) => {
        const resolvedSrc = resolveImagePath(src);
        return `![${alt}](${resolvedSrc})`;
      });
      result.push(processedLine);
    }
  }

  flushImageGroup();
  return result.join('\n');
}

// Generate unique ID for swiper instances
let swiperCounter = 0;
function generateSwiperId(): string {
  return `note-swiper-${++swiperCounter}`;
}

// Lightbox state
let lightboxSwiper: Swiper | null = null;

// Create lightbox element
function createLightbox(): HTMLElement {
  let lightbox = document.getElementById('lightbox');
  if (lightbox) return lightbox;

  lightbox = document.createElement('div');
  lightbox.id = 'lightbox';
  lightbox.className = 'lightbox';
  lightbox.innerHTML = `
    <button class="lightbox-close" aria-label="Schließen">&times;</button>
    <div class="lightbox-swiper swiper">
      <div class="swiper-wrapper"></div>
      <div class="swiper-pagination"></div>
      <div class="swiper-button-prev"></div>
      <div class="swiper-button-next"></div>
    </div>
  `;

  // Close on background click
  lightbox.addEventListener('click', (e) => {
    if (e.target === lightbox) {
      closeLightbox();
    }
  });

  // Close button
  lightbox.querySelector('.lightbox-close')?.addEventListener('click', closeLightbox);

  // Keyboard navigation
  document.addEventListener('keydown', (e) => {
    if (!lightbox?.classList.contains('active')) return;
    if (e.key === 'Escape') {
      closeLightbox();
    }
  });

  document.body.appendChild(lightbox);
  return lightbox;
}

// Open lightbox with images
function openLightbox(images: string[], startIndex: number = 0): void {
  const lightbox = createLightbox();
  const wrapper = lightbox.querySelector('.swiper-wrapper')!;

  // Build slides
  wrapper.innerHTML = images.map(src =>
    `<div class="swiper-slide"><img src="${src}" alt=""></div>`
  ).join('');

  // Show lightbox
  lightbox.classList.add('active');
  document.body.style.overflow = 'hidden';

  // Initialize or update Swiper
  if (lightboxSwiper) {
    lightboxSwiper.destroy();
  }

  lightboxSwiper = new Swiper(lightbox.querySelector('.lightbox-swiper') as HTMLElement, {
    modules: [Navigation, Pagination],
    initialSlide: startIndex,
    loop: images.length > 1,
    pagination: {
      el: lightbox.querySelector('.swiper-pagination') as HTMLElement,
      clickable: true
    },
    navigation: {
      nextEl: lightbox.querySelector('.swiper-button-next') as HTMLElement,
      prevEl: lightbox.querySelector('.swiper-button-prev') as HTMLElement
    }
  });
}

// Close lightbox
function closeLightbox(): void {
  const lightbox = document.getElementById('lightbox');
  if (lightbox) {
    lightbox.classList.remove('active');
    document.body.style.overflow = '';
  }
}

// Format date nicely
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

// Format date compact in German (e.g., "3. Januar 2026")
function formatDateCompact(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

// Extract URL slug from folder name (strip numeric prefix like "01-about" -> "about")
function extractUrlSlug(folderSlug: string): string {
  return folderSlug.replace(/^\d+-/, '');
}

// Load pages for navigation
async function loadNavigation(): Promise<void> {
  try {
    // In production, fetch from S3; in dev, fetch from local API
    const apiUrl = IS_PRODUCTION
      ? `${CONTENT_BASE}/api/pages.json`
      : '/api/pages';

    const response = await fetch(apiUrl);
    const data = await response.json();

    // Handle both old format (array) and new S3 format (items)
    let pages: Page[];
    if (data.items) {
      // S3 format: convert items to pages format
      pages = data.items.map((item: any) => ({
        slug: extractUrlSlug(item.slug),  // "02-uses" -> "uses"
        folder: item.slug,                 // Keep original for file lookup
        title: item.title || extractUrlSlug(item.slug),
        nav: item.nav === true
      }));
    } else {
      pages = data;
    }

    pagesCache = pages; // Store for later lookup

    const navPages = pages.filter(p => p.nav);
    const themeToggle = document.getElementById('theme-toggle');

    navPages.forEach(page => {
      const link = document.createElement('a');
      link.href = `/${page.slug}`;
      link.textContent = page.title;
      // Insert before theme toggle to keep toggle at the end
      if (themeToggle) {
        navEl.insertBefore(link, themeToggle);
      } else {
        navEl.appendChild(link);
      }
    });
  } catch {
    // No pages available, that's fine
  }
}

// Update active nav link
function updateActiveNav(path: string): void {
  navEl.querySelectorAll('a').forEach(link => {
    const href = link.getAttribute('href');
    link.classList.remove('active');

    if (href === path) {
      link.classList.add('active');
    } else if (path === '/' && href === '/') {
      link.classList.add('active');
    } else if (path.startsWith('/notes') && href === '/notes') {
      link.classList.add('active');
    }
  });
}

// Render a single note card
async function renderNoteCard(note: Note): Promise<string> {
  let imageHtml = '';

  if (IS_PRODUCTION && note.images && note.images.length > 0) {
    // Production: Use S3 images with srcset
    const s3Images = note.images;
    // Use 1200w version for lightbox (largest available)
    const resolvedImages = s3Images.map(img => {
      const src1200 = img.srcset.split(', ').find(s => s.includes('-1200'))?.split(' ')[0];
      return `${CONTENT_BASE}/${src1200 || img.srcset.split(' ')[0]}`;
    });
    const imagesData = JSON.stringify(resolvedImages).replace(/"/g, '&quot;');

    if (s3Images.length === 1) {
      const img = s3Images[0];
      const srcset = img.srcset.split(', ').map(s => {
        const [path, size] = s.split(' ');
        return `${CONTENT_BASE}/${path} ${size}`;
      }).join(', ');
      const src800 = img.srcset.split(', ').find(s => s.includes('-800'))?.split(' ')[0] || img.thumb;
      imageHtml = `<div class="note-images" data-images="${imagesData}">
        <img src="${CONTENT_BASE}/${src800}" srcset="${srcset}" sizes="(max-width: 600px) 400px, (max-width: 1000px) 800px, 1200px" alt="${img.alt}" class="note-image" loading="lazy" data-index="0">
      </div>`;
    } else {
      const swiperId = generateSwiperId();
      const slides = s3Images.map((img, idx) => {
        const srcset = img.srcset.split(', ').map(s => {
          const [path, size] = s.split(' ');
          return `${CONTENT_BASE}/${path} ${size}`;
        }).join(', ');
        // Use 800w version as src fallback (not thumbnail)
        const src800 = img.srcset.split(', ').find(s => s.includes('-800'))?.split(' ')[0] || img.thumb;
        return `<div class="swiper-slide">
          <img src="${CONTENT_BASE}/${src800}" srcset="${srcset}" sizes="(max-width: 600px) 400px, 800px" alt="${img.alt}" loading="lazy" data-index="${idx}">
        </div>`;
      }).join('');

      imageHtml = `
        <div class="note-images" data-images="${imagesData}">
          <div class="note-slideshow swiper" id="${swiperId}">
            <div class="swiper-wrapper">${slides}</div>
            <div class="swiper-pagination"></div>
            <div class="swiper-button-prev"></div>
            <div class="swiper-button-next"></div>
          </div>
        </div>
      `;
    }
  } else {
    // Dev mode: Extract images from content and use local paths
    const { images: contentImages } = extractImages(note.content);

    const allImages: string[] = [];
    if (note.image) {
      allImages.push(note.image);
    }
    allImages.push(...contentImages);

    const resolveImagePath = (img: string): string => {
      if (img.startsWith('http')) return img;
      return `/content/notes/${note.folder}/${img}`;
    };

    const resolvedImages = allImages.map(resolveImagePath);
    const imagesData = JSON.stringify(resolvedImages).replace(/"/g, '&quot;');

    if (allImages.length === 1) {
      const imgSrc = resolvedImages[0];
      imageHtml = `<div class="note-images" data-images="${imagesData}">
        <img src="${imgSrc}" alt="" class="note-image" loading="lazy" data-index="0">
      </div>`;
    } else if (allImages.length > 1) {
      const swiperId = generateSwiperId();
      const slides = resolvedImages.map((imgSrc, idx) => {
        return `<div class="swiper-slide"><img src="${imgSrc}" alt="" loading="lazy" data-index="${idx}"></div>`;
      }).join('');

      imageHtml = `
        <div class="note-images" data-images="${imagesData}">
          <div class="note-slideshow swiper" id="${swiperId}">
            <div class="swiper-wrapper">${slides}</div>
            <div class="swiper-pagination"></div>
            <div class="swiper-button-prev"></div>
            <div class="swiper-button-next"></div>
          </div>
        </div>
      `;
    }
  }

  const dateHtml = note.date
    ? `<time class="note-date">${formatDateCompact(note.date)}</time>`
    : '';

  const tagsHtml = note.tags.length > 0
    ? `<div class="note-tags">${note.tags.map(tag =>
        `<a href="/notes/tag/${tag}" class="note-tag">#${tag}</a>`
      ).join('')}</div>`
    : '';

  // Link to detail view
  const noteUrl = `/notes/${note.year}/${note.slug}`;

  // In list view: show only title, not full content
  const titleHtml = note.title ? `<div class="note-content"><p>${note.title}</p></div>` : '';

  return `
    <article class="note-card" data-href="${noteUrl}">
      ${imageHtml}
      ${titleHtml}
      <footer class="note-footer">
        ${dateHtml}
        ${tagsHtml}
      </footer>
    </article>
  `;
}

// Render notes list
async function renderNotesList(notes: Note[]): Promise<string> {
  const cards = await Promise.all(notes.map(renderNoteCard));
  return cards.join('');
}

// Initialize all Swiper instances in the DOM
function initSwipers(): void {
  document.querySelectorAll('.note-slideshow.swiper').forEach(el => {
    // Check if already initialized
    if ((el as any).swiper) return;

    new Swiper(el as HTMLElement, {
      modules: [Navigation, Pagination],
      loop: true,
      pagination: {
        el: el.querySelector('.swiper-pagination') as HTMLElement,
        clickable: true
      },
      navigation: {
        nextEl: el.querySelector('.swiper-button-next') as HTMLElement,
        prevEl: el.querySelector('.swiper-button-prev') as HTMLElement
      }
    });
  });

  // Add click handlers for lightbox
  initImageClickHandlers();

  // Add click handlers for note cards
  initNoteCardClickHandlers();
}

// Initialize image click handlers for lightbox
function initImageClickHandlers(): void {
  document.querySelectorAll('.note-images').forEach(container => {
    // Skip if already initialized
    if ((container as any).lightboxInit) return;
    (container as any).lightboxInit = true;

    const imagesData = container.getAttribute('data-images');
    if (!imagesData) return;

    const images: string[] = JSON.parse(imagesData);

    container.querySelectorAll('img').forEach(img => {
      img.style.cursor = 'pointer';
      img.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const index = parseInt(img.getAttribute('data-index') || '0', 10);
        openLightbox(images, index);
      });
    });
  });
}

// Initialize swipers in detail view
function initDetailSwipers(): void {
  document.querySelectorAll('.note-detail-slideshow.swiper').forEach(el => {
    if ((el as any).swiper) return;

    new Swiper(el as HTMLElement, {
      modules: [Navigation, Pagination],
      loop: true,
      pagination: {
        el: el.querySelector('.swiper-pagination') as HTMLElement,
        clickable: true
      },
      navigation: {
        nextEl: el.querySelector('.swiper-button-next') as HTMLElement,
        prevEl: el.querySelector('.swiper-button-prev') as HTMLElement
      }
    });

    // Add lightbox click handlers
    const imagesData = el.getAttribute('data-images');
    if (imagesData) {
      const images: string[] = JSON.parse(imagesData);
      el.querySelectorAll('img').forEach(img => {
        img.style.cursor = 'pointer';
        img.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const index = parseInt(img.getAttribute('data-index') || '0', 10);
          openLightbox(images, index);
        });
      });
    }
  });
}

// Initialize note card click handlers
function initNoteCardClickHandlers(): void {
  document.querySelectorAll('.note-card[data-href]').forEach(card => {
    // Skip if already initialized
    if ((card as any).clickInit) return;
    (card as any).clickInit = true;

    card.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;

      // Don't navigate if clicking on a link, image, or swiper controls
      if (target.closest('a') ||
          target.closest('img') ||
          target.closest('.swiper-button-prev') ||
          target.closest('.swiper-button-next') ||
          target.closest('.swiper-pagination')) {
        return;
      }

      const href = card.getAttribute('data-href');
      if (href) {
        window.history.pushState({}, '', href);
        router();
      }
    });
  });
}

// Format date for home list (e.g. "01 Jan, 2026")
function formatDateHome(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

// Render home page
async function renderHome(): Promise<void> {
  try {
    const [indexResponse, allNotes] = await Promise.all([
      fetch('/content/_index.md'),
      fetchAllNotes()
    ]);

    let introHtml = '';
    if (indexResponse.ok) {
      const content = await indexResponse.text();
      const { body } = parseFrontmatter(content);
      introHtml = await renderMarkdown(body);
    }

    // Notes section - simple list
    const notesData = filterNotes(allNotes, { limit: 7 });
    let notesHtml = '';
    if (notesData.notes.length > 0) {
      const listItems = notesData.notes.map(note => {
        const url = `/notes/${note.year}/${note.slug}`;
        const dateStr = note.date ? formatDateHome(note.date) : '';
        return `<li><a href="${url}">${note.title || note.slug}</a> <span class="home-note-date">${dateStr}</span></li>`;
      }).join('');

      notesHtml = `
        <section class="home-section">
          <h2 class="section-heading">Meine letzten Artikel</h2>
          <ul class="home-notes-list">${listItems}</ul>
          ${notesData.hasMore ? '<a href="/notes" class="more-link">Alle Notes &rarr;</a>' : ''}
        </section>
      `;
    }

    contentEl.innerHTML = `
      <div class="intro">${introHtml}</div>
      ${notesHtml}
    `;
  } catch (error) {
    contentEl.innerHTML = '<p>Error loading content.</p>';
    console.error('Error loading home:', error);
  }
}

// Render notes page
async function renderNotes(tag: string | null = null): Promise<void> {
  notesOffset = 0;
  currentTag = tag;

  try {
    const allNotes = await fetchAllNotes();
    const data = filterNotes(allNotes, { tag, limit: NOTES_PER_PAGE });

    const titleHtml = tag
      ? `<h1>Notes <span class="tag-filter">#${tag}</span></h1><a href="/notes" class="clear-filter">Filter entfernen</a>`
      : '<h1>Notes</h1>';

    const notesList = await renderNotesList(data.notes);

    const loadMoreHtml = data.hasMore
      ? '<button id="load-more" class="load-more-btn">Mehr laden</button>'
      : '';

    contentEl.innerHTML = `
      <div class="notes-header">${titleHtml}</div>
      <div id="notes-container" class="notes-list">${notesList}</div>
      ${loadMoreHtml}
    `;

    notesOffset = data.notes.length;

    // Initialize slideshows
    initSwipers();

    // Attach load more handler
    const loadMoreBtn = document.getElementById('load-more');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', loadMoreNotes);
    }
  } catch (error) {
    contentEl.innerHTML = '<p>Error loading notes.</p>';
    console.error('Error loading notes:', error);
  }
}

// Load more notes
async function loadMoreNotes(): Promise<void> {
  const loadMoreBtn = document.getElementById('load-more') as HTMLButtonElement;
  if (loadMoreBtn) {
    loadMoreBtn.disabled = true;
    loadMoreBtn.textContent = 'Laden...';
  }

  try {
    const allNotes = await fetchAllNotes();
    const data = filterNotes(allNotes, { tag: currentTag, limit: NOTES_PER_PAGE, offset: notesOffset });

    const container = document.getElementById('notes-container');
    if (container) {
      const newNotes = await renderNotesList(data.notes);
      container.insertAdjacentHTML('beforeend', newNotes);
      // Initialize any new slideshows
      initSwipers();
    }

    notesOffset += data.notes.length;

    if (loadMoreBtn) {
      if (data.hasMore) {
        loadMoreBtn.disabled = false;
        loadMoreBtn.textContent = 'Mehr laden';
      } else {
        loadMoreBtn.remove();
      }
    }
  } catch (error) {
    console.error('Error loading more notes:', error);
    if (loadMoreBtn) {
      loadMoreBtn.disabled = false;
      loadMoreBtn.textContent = 'Mehr laden';
    }
  }
}

// Render single page
async function renderPage(slug: string): Promise<void> {
  try {
    // Find page in cache to get folder path
    const page = pagesCache.find(p => p.slug === slug);
    if (!page) {
      contentEl.innerHTML = '<p>Seite nicht gefunden.</p>';
      return;
    }

    if (IS_PRODUCTION) {
      // Production: Fetch from S3
      const response = await fetch(`${CONTENT_BASE}/pages/${page.folder}/index.json`);
      if (!response.ok) {
        contentEl.innerHTML = '<p>Seite nicht gefunden.</p>';
        return;
      }

      const pageData = await response.json();

      // Handle encrypted content
      let htmlContent = pageData.content;
      if (pageData.encrypted) {
        const password = await showPasswordPrompt(pageData.title || 'Geschützte Seite');
        if (!password) {
          contentEl.innerHTML = '<p>Zugriff verweigert.</p>';
          return;
        }
        try {
          htmlContent = await decryptContent(pageData.content, password);
        } catch {
          contentEl.innerHTML = '<p>Falsches Passwort.</p>';
          return;
        }
      }

      // Resolve image paths for S3
      const resolveImagePath = (img: string): string => {
        if (img.startsWith('http')) return img;
        const baseName = img.replace(/\.[^.]+$/, '');
        return `${CONTENT_BASE}/pages/${page.folder}/${baseName}-800.webp`;
      };

      // Process HTML content to resolve image paths
      const html = htmlContent.replace(
        /<img([^>]*)src="([^"]+)"([^>]*)>/g,
        (_match: string, before: string, src: string, after: string) => {
          if (src.startsWith('http')) return _match;
          const resolvedSrc = resolveImagePath(src);
          return `<img${before}src="${resolvedSrc}"${after}>`;
        }
      );

      contentEl.innerHTML = `
        <article>
          <h1>${pageData.title || slug}</h1>
          ${html}
        </article>
      `;
    } else {
      // Dev: Fetch markdown file
      const response = await fetch(`/content/pages/${page.folder}/_index.md`);
      if (!response.ok) {
        contentEl.innerHTML = '<p>Seite nicht gefunden.</p>';
        return;
      }

      const content = await response.text();
      const { meta, body } = parseFrontmatter(content);

      // Resolve relative image paths
      const resolveImagePath = (img: string): string => {
        if (img.startsWith('http')) return img;
        return `/content/pages/${page.folder}/${img}`;
      };

      // Process content to resolve image paths
      const processedBody = body.replace(
        /!\[([^\]]*)\]\(([^)]+)\)/g,
        (_match, alt, src) => `![${alt}](${resolveImagePath(src)})`
      );

      const html = await renderMarkdown(processedBody);

      contentEl.innerHTML = `
        <article>
          <h1>${meta.title || slug}</h1>
          ${html}
        </article>
      `;
    }
  } catch (error) {
    contentEl.innerHTML = '<p>Error loading page.</p>';
    console.error('Error loading page:', error);
  }
}

// Render single note detail view
async function renderNoteDetail(year: string, slug: string): Promise<void> {
  try {
    // In production, fetch from S3; in dev, fetch from local API
    const apiUrl = IS_PRODUCTION
      ? `${CONTENT_BASE}/notes/${year}/${slug}/index.json`
      : `/api/note/${year}/${encodeURIComponent(slug)}`;

    const response = await fetch(apiUrl);
    if (!response.ok) {
      contentEl.innerHTML = '<p>Note nicht gefunden.</p>';
      return;
    }

    const note: Note = await response.json();

    // Resolve image paths based on environment
    const resolveImagePath = (img: string): string => {
      if (img.startsWith('http')) return img;
      if (IS_PRODUCTION) {
        // In production, images are already WebP in S3
        const baseName = img.replace(/\.[^.]+$/, '');
        return `${CONTENT_BASE}/notes/${note.folder}/${baseName}-800.webp`;
      }
      return `/content/notes/${note.folder}/${img}`;
    };

    let html: string;

    if (IS_PRODUCTION) {
      // Production: content is already HTML, just resolve image paths
      // S3 JSON uses "path" (e.g., "notes/2026/slug"), not "folder"
      const notePath = (note as any).path || `notes/${year}/${slug}`;

      // Handle both ./filename and filename (without ./) patterns
      html = note.content.replace(
        /src="(?:\.\/)?([^"\/][^"]+)"/g,
        (_match, src) => {
          // Skip if already absolute URL
          if (src.startsWith('http')) return _match;
          // Convert to WebP if not already
          const resolvedSrc = src.match(/-\d+\.webp$/)
            ? src
            : src.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '-800.webp');
          return `src="${CONTENT_BASE}/${notePath}/${resolvedSrc}"`;
        }
      );
    } else {
      // Dev: content is markdown, process and render
      const processedContent = processConsecutiveImages(note.content, resolveImagePath);
      html = await renderMarkdown(processedContent);
    }

    // Header image if present
    let headerImageHtml = '';
    if (note.image) {
      const imgSrc = resolveImagePath(note.image);
      headerImageHtml = `<img src="${imgSrc}" alt="" class="note-detail-image">`;
    }

    const tagsHtml = note.tags.length > 0
      ? `<div class="note-detail-tags">${note.tags.map(tag =>
          `<a href="/notes/tag/${tag}" class="note-tag">#${tag}</a>`
        ).join('')}</div>`
      : '';

    document.title = note.title ? `${note.title} - larsolino.io` : 'Note - larsolino.io';

    contentEl.innerHTML = `
      <article class="note-detail">
        ${headerImageHtml}
        ${note.title ? `<h1>${note.title}</h1>` : ''}
        ${note.date ? `<p class="note-detail-date">${formatDate(note.date)}</p>` : ''}
        <div class="note-detail-content">${html}</div>
        ${tagsHtml}
        <a href="/notes" class="back-link">&larr; Alle Notes</a>
      </article>
    `;

    // Initialize swipers for consecutive image groups
    initDetailSwipers();
  } catch (error) {
    contentEl.innerHTML = '<p>Error loading note.</p>';
    console.error('Error loading note:', error);
  }
}

// Router
async function router(): Promise<void> {
  const path = window.location.pathname;
  updateActiveNav(path);

  if (path === '/' || path === '') {
    document.title = 'larsolino.io';
    await renderHome();
  } else if (path === '/notes') {
    document.title = 'Notes - larsolino.io';
    await renderNotes();
  } else if (path.startsWith('/notes/tag/')) {
    const tag = decodeURIComponent(path.replace('/notes/tag/', ''));
    document.title = `#${tag} - Notes - larsolino.io`;
    await renderNotes(tag);
  } else if (path.match(/^\/notes\/\d{4}\/[^/]+$/)) {
    // Note detail: /notes/2025/slug
    const parts = path.split('/');
    const year = parts[2];
    const slug = decodeURIComponent(parts[3]);
    await renderNoteDetail(year, slug);
  } else {
    // Try to load as a page
    const slug = path.replace(/^\//, '');
    await renderPage(slug);
  }
}

// Handle navigation
function handleClick(event: MouseEvent): void {
  const target = event.target as HTMLElement;
  const anchor = target.closest('a');

  if (!anchor) return;

  const href = anchor.getAttribute('href');
  if (!href) return;

  // Skip external links and special files
  if (href.startsWith('http://') || href.startsWith('https://')) return;
  if (href.endsWith('.xml') || href.endsWith('.json')) return;

  event.preventDefault();
  window.history.pushState({}, '', href);
  router();
}

// Theme toggle
function initTheme(): void {
  const savedTheme = localStorage.getItem('theme');
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const theme = savedTheme || (prefersDark ? 'dark' : 'light');
  document.documentElement.setAttribute('data-theme', theme);

  const toggle = document.getElementById('theme-toggle');
  if (toggle) {
    toggle.addEventListener('click', () => {
      const current = document.documentElement.getAttribute('data-theme');
      const next = current === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      localStorage.setItem('theme', next);
    });
  }
}

// Mobile burger menu
function initBurgerMenu(): void {
  const burger = document.getElementById('burger');
  const nav = document.getElementById('nav');
  if (!burger || !nav) return;

  // Create overlay
  let overlay = document.querySelector('.nav-overlay') as HTMLElement;
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.className = 'nav-overlay';
    document.body.appendChild(overlay);
  }

  const toggleMenu = () => {
    burger.classList.toggle('active');
    nav.classList.toggle('open');
    overlay.classList.toggle('open');
    document.body.style.overflow = nav.classList.contains('open') ? 'hidden' : '';
  };

  const closeMenu = () => {
    burger.classList.remove('active');
    nav.classList.remove('open');
    overlay.classList.remove('open');
    document.body.style.overflow = '';
  };

  burger.addEventListener('click', toggleMenu);
  overlay.addEventListener('click', closeMenu);

  // Close menu when clicking a nav link
  nav.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', closeMenu);
  });
}

// Initialize
async function init(): Promise<void> {
  initTheme();
  initBurgerMenu();
  await loadNavigation();
  await router();

  // Handle navigation clicks
  document.addEventListener('click', handleClick);

  // Handle browser back/forward
  window.addEventListener('popstate', router);
}

init();
